import React, { useState } from "react";
import ShippingForm, { ShippingRate } from "../../components/CheckOut/ShippingForm";
import StripeCheckoutForm from "../../components/CheckOut/StripeCheckoutForm";
import { useCart } from "../../context/CartContext";
import { getApiBase } from "../../lib/api";
import type { StripeTotals } from "./Cart";

type Props = {
  onReadyForPayment: (payload: { totals: StripeTotals; clientSecret: string }) => void;
  clientSecret: string | null;
};

const ShippingStep: React.FC<Props> = ({ onReadyForPayment, clientSecret }) => {
  const { cart } = useCart();

  const [shippingData, setShippingData] = useState({
    fullName: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone_number: "",
  });

  const [selectedRate, setSelectedRate] = useState<ShippingRate | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setShippingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = async () => {
    if (!selectedRate) {
      alert("Please select a shipping option.");
      return;
    }

    if (!shippingData.email || !shippingData.fullName) {
      alert("Please enter your name and email.");
      return;
    }

    if (!shippingData.phone_number.trim()) {
      alert("Please enter your phone number.");
      return;
    }

    if (
      !shippingData.address ||
      !shippingData.city ||
      !shippingData.state ||
      !shippingData.zip
    ) {
      alert("Please complete your shipping address.");
      return;
    }

    setLoading(true);

    try {
      const parsedRate = Number.parseFloat(selectedRate.amount);

      const formattedShipping = {
        to_name: shippingData.fullName,
        to_address: shippingData.address,
        to_city: shippingData.city,
        to_state: shippingData.state,
        to_zip: shippingData.zip,
        to_country: "US",
        email: shippingData.email || null,
        phone_number: shippingData.phone_number.trim(),

        rate_object_id: selectedRate.object_id,

        shipping_rate: Number.isFinite(parsedRate) ? parsedRate : null,
        rate: Number.isFinite(parsedRate) ? parsedRate : null,

        carrier: selectedRate.provider || null,
        label_url: null,
        tracking_number: null,
      };

      localStorage.setItem("shippingData", JSON.stringify(formattedShipping));
      localStorage.setItem("shippingRate", JSON.stringify(selectedRate));

      const res = await fetch(`${getApiBase()}/checkout/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart: cart.map((i) => ({
            id: i.id,
            variant_id: i.variant_id || null,
            size_label: i.size_label || null,
            quantity: i.quantity,
            price: i.price,
          })),
          shippingData: formattedShipping,
          shippingRate: Number.isFinite(parsedRate) ? parsedRate : 0,
        }),
      });

      const text = await res.text();
      let data: any;

      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || "Server returned a non-JSON response");
      }

      if (!res.ok) {
        console.error("Create payment intent failed:", data);
        throw new Error(
          data?.error ||
            data?.message ||
            `Failed to create payment intent (${res.status})`
        );
      }

      if (!data?.clientSecret || !data?.totals) {
        throw new Error("Missing clientSecret/totals from server response.");
      }

      localStorage.setItem("stripeTotals", JSON.stringify(data.totals));
      localStorage.setItem("stripeClientSecret", data.clientSecret);

      onReadyForPayment({
        totals: data.totals,
        clientSecret: data.clientSecret,
      });

      setShowPaymentForm(true);
    } catch (err: any) {
      alert(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full border border-[rgba(92,47,47,0.2)] p-6 rounded shadow-sm bg-white/90 backdrop-blur-sm">
      <ShippingForm
        shippingData={shippingData}
        onChange={handleShippingChange}
        onShippingRateSelected={setSelectedRate}
      />

      {!showPaymentForm && (
        <button
          onClick={handleNext}
          className={`mt-6 w-full py-2 rounded font-semibold transition ${
            selectedRate
              ? "bg-[#5c2f2f] text-white hover:bg-[#3f1f1f]"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
          disabled={!selectedRate || loading}
        >
          {loading ? "Calculating..." : "Continue to Payment"}
        </button>
      )}

      {showPaymentForm && (
        <div className="mt-8">
          <StripeCheckoutForm clientSecret={clientSecret || undefined} />
        </div>
      )}
    </div>
  );
};

export default ShippingStep;
